/**
 * Frontend permission guard — determines feature access by role.
 *
 * Roles:
 *   platform_admin — hello@zentrobooks.com ONLY. Can access /admin/*
 *   owner          — tenant owner. Full app access (transactions, reports, staff, etc.)
 *   viewer         — read-only (future use)
 */

export type FeatureKey =
  | "canViewDashboard"
  | "canManageTransactions"
  | "canViewReports"
  | "canManageReports"
  | "canManageBilling"
  | "canManageSubscriptions"
  | "canManageClients"
  | "canManageAppointments"
  | "canAccessAdminDashboard"
  | "canManageAnnouncements"
  | "canAccessCompliance"
  | "canAccessSupport"
  | "canManageInventory"
  | "canManageBudgets"
  | "canManagePayroll"
  | "canAccessAIAssistant"
  | "canManageTaxFilings"
  | "canManageLegalDocs"
  | "canManageStaff";

export type UserRole = "platform_admin" | "owner" | "viewer";

interface UserForPermission {
  id: number;
  email: string;
  username: string;
  isAdmin?: boolean;
}

/**
 * Is this user the platform administrator?
 * Exactly ONE account — hello@zentrobooks.com.
 */
export function isPlatformAdmin(user: UserForPermission | null): boolean {
  if (!user) return false;
  return user.email === "hello@zentrobooks.com";
}

/**
 * Derive role from user profile.
 * platform_admin = hello@zentrobooks.com
 * owner = every other authenticated user (tenant owner)
 */
export function getUserRole(user: UserForPermission | null): UserRole {
  if (!user) return "viewer";
  if (isPlatformAdmin(user)) return "platform_admin";
  return "owner";
}

/**
 * Permission matrix.
 * platform_admin: admin dashboard + all management
 * owner: full tenant app (NO admin dashboard)
 */
const permissionMatrix: Record<UserRole, Set<FeatureKey>> = {
  platform_admin: new Set([
    "canViewDashboard",
    "canManageTransactions",
    "canViewReports",
    "canManageReports",
    "canManageBilling",
    "canManageSubscriptions",
    "canManageClients",
    "canManageAppointments",
    "canAccessAdminDashboard",
    "canManageAnnouncements",
    "canAccessCompliance",
    "canAccessSupport",
    "canManageInventory",
    "canManageBudgets",
    "canManagePayroll",
    "canAccessAIAssistant",
    "canManageTaxFilings",
    "canManageLegalDocs",
    "canManageStaff",
  ]),
  owner: new Set([
    "canViewDashboard",
    "canManageTransactions",
    "canViewReports",
    "canManageReports",
    "canManageBilling",
    "canManageInventory",
    "canManageBudgets",
    "canManagePayroll",
    "canAccessAIAssistant",
    "canManageTaxFilings",
    "canManageLegalDocs",
    "canManageStaff",
  ]),
  viewer: new Set([
    "canViewDashboard",
    "canViewReports",
  ]),
};

/**
 * Check if a user can access a specific feature.
 */
export function canAccess(feature: FeatureKey, user: UserForPermission | null): boolean {
  const role = getUserRole(user);
  return permissionMatrix[role].has(feature);
}

/**
 * Get all features a user has access to.
 */
export function getAccessibleFeatures(user: UserForPermission | null): FeatureKey[] {
  const role = getUserRole(user);
  return Array.from(permissionMatrix[role]);
}

/**
 * Route-to-feature mapping for automatic route protection.
 */
export const routePermissions: Record<string, FeatureKey> = {
  "/dashboard": "canViewDashboard",
  "/transactions": "canManageTransactions",
  "/transaction-register": "canManageTransactions",
  "/bank-reconciliation": "canManageTransactions",
  "/reports": "canViewReports",
  "/reports-simple": "canViewReports",
  "/reports-dashboard": "canViewReports",
  "/reports-new": "canViewReports",
  "/reports/jasper": "canManageReports",
  "/reports/advanced": "canManageReports",
  "/profit-loss": "canViewReports",
  "/balance-sheet": "canViewReports",
  "/admin/client-management": "canManageClients",
  "/admin/dashboard": "canAccessAdminDashboard",
  "/admin/compliance": "canAccessCompliance",
  "/admin/support": "canAccessSupport",
  "/staff": "canManageStaff",
};

/**
 * Check if a user can access a specific route.
 */
export function canAccessRoute(path: string, user: UserForPermission | null): boolean {
  const feature = routePermissions[path];
  if (!feature) return true; // No explicit restriction
  return canAccess(feature, user);
}
